/**
 * R0627-08 — direct-to-blob PUT of an AI-model weights file (`.pt`).
 *
 * Mirrors the AI3 desktop client's `AzureBlobUploader` (`_AI3/src/ai3/app/
 * adapters/blob_uploader.py`): a single `PUT` of the whole file to the
 * write-scoped (`cw`) SAS URL minted by `mintAiModelArtifactUploadUrl`, with
 * the mandatory `x-ms-blob-type: BlockBlob` header. Bytes go STRAIGHT to the
 * blob endpoint — they never traverse the gateway.
 *
 * The minted `uploadUrl` is a PREFIX SAS (its path ends at the artifact
 * prefix `…/artifact/`, then the `?sv=…` SAS query). Per the contract the
 * client splices the weights filename into the path BEFORE the `?` and PUTs to
 * that. The persisted `artifactUri` is the SAS-free `blobBaseUrl` + the same
 * filename, so a later reader (the inference epic) addresses the same blob.
 *
 * `XMLHttpRequest` (not `fetch`) is used deliberately: it is the only browser
 * primitive that reports UPLOAD progress (`xhr.upload.onprogress`), which the
 * dialog surfaces for the large (tens–hundreds of MB) weights files. A single
 * PUT is within Azure's block-blob single-PUT ceiling for these sizes (and is
 * what Azurite accepts locally).
 */
import type { AiModelArtifactUploadTarget } from "@aeriosiq/types";

export interface UploadArtifactArgs {
  target: AiModelArtifactUploadTarget;
  file: File;
  /** Called with an integer 0-100 as the upload streams. */
  onProgress?: (pct: number) => void;
  /** Abort the in-flight PUT. */
  signal?: AbortSignal;
}

export interface UploadArtifactResult {
  /** The SAS-free blob URL to persist into the version's `artifactUri`. */
  artifactUri: string;
  /** The uploaded file's name (for the Artifact column / status copy). */
  fileName: string;
}

/** Azure REST API version pinned for the block-blob PUT (matches AI3). */
const AZURE_API_VERSION = "2024-11-04";

/**
 * Splice `filename` into a prefix SAS URL's path, before the `?` query string.
 * `https://acct/cont/prefix/?sv=…` + `weights.pt` →
 * `https://acct/cont/prefix/weights.pt?sv=…`.
 */
export function spliceFilenameIntoUploadUrl(
  uploadUrl: string,
  filename: string,
): string {
  const qIdx = uploadUrl.indexOf("?");
  const base = qIdx >= 0 ? uploadUrl.slice(0, qIdx) : uploadUrl;
  const query = qIdx >= 0 ? uploadUrl.slice(qIdx) : "";
  const sep = base.endsWith("/") ? "" : "/";
  return `${base}${sep}${encodeURIComponent(filename)}${query}`;
}

/** Join the SAS-free `blobBaseUrl` with the filename → the persisted `artifactUri`. */
export function buildArtifactUri(
  blobBaseUrl: string,
  filename: string,
): string {
  const sep = blobBaseUrl.endsWith("/") ? "" : "/";
  return `${blobBaseUrl}${sep}${encodeURIComponent(filename)}`;
}

/**
 * PUT `file` to the minted SAS target and return the `artifactUri` to persist.
 * Rejects (Error) on any non-2xx / network error; rejects with an
 * `AbortError`-named DOMException when `signal` aborts.
 */
export function uploadArtifactToBlob(
  args: UploadArtifactArgs,
): Promise<UploadArtifactResult> {
  const { target, file, onProgress, signal } = args;
  const blobUrl = spliceFilenameIntoUploadUrl(target.uploadUrl, file.name);

  return new Promise<UploadArtifactResult>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Upload aborted", "AbortError"));
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.open("PUT", blobUrl, true);
    xhr.setRequestHeader("x-ms-blob-type", "BlockBlob");
    xhr.setRequestHeader("x-ms-version", AZURE_API_VERSION);
    xhr.setRequestHeader(
      "Content-Type",
      file.type && file.type.length > 0
        ? file.type
        : "application/octet-stream",
    );

    const onAbort = (): void => {
      xhr.abort();
    };
    if (signal) signal.addEventListener("abort", onAbort);
    const cleanup = (): void => {
      if (signal) signal.removeEventListener("abort", onAbort);
    };

    if (onProgress) {
      xhr.upload.onprogress = (e: ProgressEvent): void => {
        if (e.lengthComputable && e.total > 0) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
    }
    xhr.onload = (): void => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100);
        resolve({
          artifactUri: buildArtifactUri(target.blobBaseUrl, file.name),
          fileName: file.name,
        });
      } else {
        reject(
          new Error(
            `Blob upload failed (${String(xhr.status)} ${xhr.statusText}).`,
          ),
        );
      }
    };
    xhr.onerror = (): void => {
      cleanup();
      reject(new Error("Blob upload failed (network error)."));
    };
    xhr.onabort = (): void => {
      cleanup();
      reject(new DOMException("Upload aborted", "AbortError"));
    };

    xhr.send(file);
  });
}
